import { countriesQuery } from '@/api/countries/countryQueries';
import { Button } from '@/components/common/Button';
import { DialogWrapper } from '@/components/common/DialogWrapper';
import { Input } from '@/components/common/Input';
import { PhoneInput } from '@/components/common/PhoneInput';
import { Select } from '@/components/common/Select';
import type { User } from '@/domain/User';
import { defaultAddressKey } from '@/domain/User';
import { api } from '@/lib/api';
import { validatePhoneNumber } from '@/lib/validation/phoneValidation';
import { validateZipcode } from '@/lib/validation/zipcodeValidation';
import { useQuery } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { toFormikValidate } from 'zod-formik-adapter';

function buildSchema(needsPhone: boolean, needsAddress: boolean) {
  return z
    .object({
      phone: needsPhone
        ? z
            .string()
            .min(1, 'Phone number is required')
            .refine((val) => validatePhoneNumber(val), {
              message: 'Invalid phone number format',
            })
        : z.string().optional(),
      country: needsAddress
        ? z.string().min(1, 'Country is required')
        : z.string().optional(),
      city: needsAddress
        ? z.string().min(1, 'City is required')
        : z.string().optional(),
      line1: needsAddress
        ? z.string().min(1, 'Address line is required')
        : z.string().optional(),
      zipcode: needsAddress
        ? z.string().min(1, 'Zip code is required')
        : z.string().optional(),
    })
    .refine(
      (data) =>
        !needsAddress ||
        validateZipcode(data.zipcode ?? '', data.country ?? ''),
      {
        message: 'Invalid zipcode for the selected country',
        path: ['zipcode'],
      },
    );
}

interface CompleteProfileFormValues {
  phone: string;
  country: string;
  city: string;
  line1: string;
  zipcode: string;
}

interface CompleteProfileDialogProps {
  open: boolean;
  accessToken: string;
  refreshToken: string;
  user: User | null;
  onComplete: () => void;
}

export function CompleteProfileDialog({
  open,
  accessToken,
  refreshToken,
  user,
  onComplete,
}: CompleteProfileDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: countriesData } = useQuery(countriesQuery);

  const apiWithToken = api.extend({
    hooks: {
      beforeRequest: [
        (request) => {
          request.headers.set('Authorization', `Bearer ${accessToken}`);
        },
      ],
    },
  });

  const needsPhone = !user?.phone || user.phone.includes('temp');
  const needsAddress = !user?.addresses || user.addresses.length === 0;

  const countryOptions = countriesData?.data
    ? countriesData.data.map((country) => ({
        label: country.country,
        value: country.country_code,
      }))
    : [];

  const formik = useFormik<CompleteProfileFormValues>({
    initialValues: {
      phone: '',
      country: '',
      city: '',
      line1: '',
      zipcode: '',
    },
    enableReinitialize: true,
    validate: toFormikValidate(buildSchema(needsPhone, needsAddress)),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        if (needsPhone) {
          await apiWithToken
            .put('user', { json: { phone: values.phone } })
            .json();
        }

        if (needsAddress) {
          const countryName = countriesData?.data.find(
            (c) => c.country_code === values.country,
          )?.country;

          await apiWithToken
            .post('addresses', {
              json: {
                name: defaultAddressKey,
                country: countryName || values.country,
                city: values.city,
                line1: values.line1,
                zipcode: values.zipcode,
              },
            })
            .json();
        }

        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('refresh_token', refreshToken);
        onComplete();
      } catch {
        toast.error('Failed to update profile. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <DialogWrapper
      open={open}
      onOpenChange={() => {}}
      title="Complete your profile"
      maxWidth="xl"
      showCloseButton={false}
    >
      <p className="text-muted-foreground mb-4 text-sm">
        Please fill in your details to continue.
      </p>
      <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
        {needsPhone && (
          <PhoneInput
            id="complete-phone"
            label="Phone number"
            name="phone"
            value={formik.values.phone}
            onChange={(val) => formik.setFieldValue('phone', val)}
            onBlur={formik.handleBlur}
            error={
              formik.touched.phone && formik.errors.phone
                ? formik.errors.phone
                : undefined
            }
            touched={!!formik.touched.phone}
          />
        )}

        {needsAddress && (
          <>
            <Select
              id="complete-country"
              label="Country"
              name="country"
              value={formik.values.country}
              onChange={(val: string) => formik.setFieldValue('country', val)}
              onBlur={formik.handleBlur}
              options={countryOptions}
              error={
                formik.touched.country && formik.errors.country
                  ? formik.errors.country
                  : undefined
              }
              touched={!!formik.touched.country}
            />
            <Input
              id="complete-line1"
              label="Address Line"
              type="text"
              {...formik.getFieldProps('line1')}
              error={
                formik.touched.line1 && formik.errors.line1
                  ? formik.errors.line1
                  : undefined
              }
              touched={!!formik.touched.line1}
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                id="complete-city"
                label="City"
                type="text"
                {...formik.getFieldProps('city')}
                error={
                  formik.touched.city && formik.errors.city
                    ? formik.errors.city
                    : undefined
                }
                touched={!!formik.touched.city}
              />
              <Input
                id="complete-zipcode"
                label="Zip / Postal Code"
                type="text"
                {...formik.getFieldProps('zipcode')}
                error={
                  formik.touched.zipcode && formik.errors.zipcode
                    ? formik.errors.zipcode
                    : undefined
                }
                touched={!!formik.touched.zipcode}
              />
            </div>
          </>
        )}

        <Button
          type="submit"
          variant="secondary"
          className="hover:bg-secondary h-14.5 text-base text-white transition-all duration-500"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Continue'}
        </Button>
      </form>
    </DialogWrapper>
  );
}
